import type { UseInfiniteQueryOptions } from '@tanstack/react-query';

import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useMemo } from 'react';
import { getAccessToken } from 'src/shared/utils/getAccessToken';
import { paramsSerializer } from 'src/shared/utils/paramsSerializer';

export type PaginatedResponse<T> = { next: string; results: T[] } | T[];

export type UsePaginatedQueryProps<T> = {
  enabled?: boolean;
  headers?: Record<string, string>;
  includePageSizeInRequest?: boolean;
  pageSize?: number;
  url: string;
  urlParams?: Record<string, unknown>;
  withAuthorization?: boolean;
} & Pick<UseInfiniteQueryOptions<T>, 'queryKey'>;

type PageParam = { initial: true; url: string };

export const usePaginatedQuery = <T>({
  enabled,
  headers,
  includePageSizeInRequest = true,
  pageSize,
  queryKey,
  url,
  urlParams,
  withAuthorization = true,
}: UsePaginatedQueryProps<T>) => {
  const negotiatedQueryKey = useMemo(
    () =>
      [
        ...(Array.isArray(queryKey) ? queryKey : [queryKey]),
        urlParams ? JSON.stringify(urlParams) : undefined,
      ].filter(Boolean),
    [queryKey, urlParams]
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    refetch,
    status,
    ...useInfiniteQueryProps
  } = useInfiniteQuery<PaginatedResponse<T>>({
    enabled,
    getNextPageParam: (lastPage) => {
      return 'next' in lastPage && lastPage.next
        ? { url: lastPage.next }
        : null;
    },
    initialPageParam: { initial: true, url },
    queryFn: async ({ pageParam }) => {
      const queryFnPageParams = pageParam as PageParam;
      const queryHeaders: Record<string, string> = { ...headers };

      if (withAuthorization) {
        const token = getAccessToken();
        if (token) {
          queryHeaders.Authorization = `Bearer ${token}`;
        }
      }

      const params: Record<string, unknown> = {
        page_size:
          includePageSizeInRequest && queryFnPageParams.initial
            ? pageSize
            : undefined,
        ...(queryFnPageParams.initial ? urlParams : {}),
      };

      const { data } = await axios.get<PaginatedResponse<T>>(
        queryFnPageParams.url,
        {
          headers: queryHeaders,
          params,
          paramsSerializer,
        }
      );
      return data;
    },
    queryKey: negotiatedQueryKey,
  });

  const items = useMemo(
    () =>
      (data?.pages.reduce(
        (prev: T[], curr) => [
          ...prev,
          ...('results' in curr ? curr.results : curr),
        ],
        []
      ) || []) as T[],
    [data?.pages]
  );

  const fetchNextPageHandler = useCallback(async () => {
    if (!hasNextPage || status !== 'success') {
      return;
    }
    return await fetchNextPage();
  }, [fetchNextPage, hasNextPage, status]);

  const isEmpty = useInfiniteQueryProps.isSuccess && !items.length;

  return {
    fetchNextPage: fetchNextPageHandler,
    hasNextPage,
    isEmpty,
    items,
    queryKey: negotiatedQueryKey,
    refetch,
    status,
    ...useInfiniteQueryProps,
  };
};
